<template>
	<v-chart
		class="chart"
		:option="option"
		ref="chart"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>

<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
import VChart from 'vue-echarts';
import { use } from 'echarts/core'
import { GaugeChart } from 'echarts/charts'
import { TitleComponent } from 'echarts/components'
import { CanvasRenderer, SVGRenderer, } from 'echarts/renderers'

use([
	TitleComponent,
	GaugeChart,
	CanvasRenderer,
	SVGRenderer,
])

export default {
	GREEN: '#73bf69',
	RED: '#f2495c',
	GRAY: '#ccccdc',
	TITLE_COLOR: '#2c3e50',
	// Center position Default 50%, 50% 
	CENTER_HORIZONTAL: '50%',
	CENTER_VERTICAL: '75%',
	// Round start, end
	START_ANGLE: 200,
	END_ANGLE: -20,
	//
	CHART_MIN_VALUE: 0,
	CHART_MAX_VALUE: 100,
	CHART_RANGE: 100,
	PROGRESS_LINE_WIDTH: 15,

	props: {
		value: {
			type: [Number, String],
			require: false,
			default: 0,
		},
	},

	components: {
		VChart,
	},

	setup() {
		const unit = 'А'
		return {
			unit,
		}
	},

	data() {
		return {
			// Graph configuration
			option: {
				title: {
					text: 'Вихідний струм',
					left: 'center',
					top: '3%',
					textStyle: {
						color: this.$options.TITLE_COLOR,
						fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
						fontStyle: 'normal',
						fontSize: 14.28,
						fontWeight: 700,
					},
				},
				series: [
					// Value ring
					{
						type: 'gauge',
						zlevel: 1,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						// The number of split segments of gauge chart scale
						// splitNumber: this.$options.CHART_RANGE <= this.$options.MAX_CHART_SPLIT_SEGMENTS
						// 	? this.$options.CHART_RANGE
						// 	: this.$options.MAX_CHART_SPLIT_SEGMENTS,
						splitNumber: 5,
						itemStyle: {
							color: this.$options.GREEN,
						},
						progress: {
							show: true,
							overlap: true,
							width: this.$options.PROGRESS_LINE_WIDTH,
						},
						pointer: {
							show: false, // Remove pointer
						},

						// Line behind the chart round
						axisLine: {
							lineStyle: {
								width: -2,
								color: [[1,this.$options.GREEN],],
								// shadowColor: 'rgba(0, 0, 0, 0.5)',
								// shadowBlur: 20
							},
						},
						axisTick: {
							show: false, // Remove tics
							distance: -8,
							splitNumber: 5,
							lineStyle: {
								width: 1,
								color: this.$options.GRAY
							}
						},
						splitLine: {
							show: true,
							distance: -4,
							length: 4,
							lineStyle: {
								width: 2,
								color: '#999',
							}
						},
						axisLabel: {
							distance: -22,
							color: this.$options.TITLE_COLOR,
							fontSize: 12,
						},
						anchor: {
							show: false
						},
						title: {
							show: false
						},
						detail: {
							valueAnimation: true,
							width: '60%',
							lineHeight: 10,
							borderRadius: 8,
							offsetCenter: [0, '20%'],
							fontSize: 18,
							fontWeight: 'bolder',
							formatter: () =>
								this.chartValue == undefined || isNaN(this.chartValue) || this.chartValue == '' ? '-' : `${this.chartValue} ${this.unit}`,
							color: 'inherit'
						},
						data: [
							{
								value: 0
							}
						]
					},
					// Gray ring
					{
						type: 'gauge',
						zlevel: 0,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						splitNumber: 0, // The number of split segments of gauge chart scale
						itemStyle: {
							color: '#f4f5f5' // gray
						},
						progress: {
							show: true,
							overlap: true,
							width: this.$options.PROGRESS_LINE_WIDTH,
						},
						pointer: {
							show: false
						},
						axisLine: {
							show: false
						},
						axisTick: {
							show: false
						},
						splitLine: {
							show: false
						},
						axisLabel: {
							show: false
						},
						detail: {
							show: false
						},
						data: [
							{
								value: this.$options.CHART_MAX_VALUE,
							}
						]
					},

					// White spaser ring
					{
						type: 'gauge',
						zlevel: 2,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						splitNumber: 0, // The number of split segments of gauge chart scale
						itemStyle: {
							color: '#ffffff' // white
						},
						progress: {
							show: true,
							overlap: true,
							width: 2
						},
						pointer: {
							show: false
						},
						axisLine: {
							show: false
						},
						axisTick: {
							show: false
						},
						splitLine: {
							show: false
						},
						axisLabel: {
							show: false
						},
						detail: {
							show: false
						},
						data: [
							{
								value: this.$options.CHART_MAX_VALUE,
							}
						]
					},
				]
			},
		};
	},

	watch: {
		// Update chart on value update
		value(/*newCriterias*/) {
			//console.log('update value')
			const series = this.option.series[0]
			series.data[0].value = this.chartValue
		}
	},

	created() {},

	mounted() {
		//
		const series = this.option.series[0]
		series.data[0].value = this.chartValue	// set value
	},
	
	methods: {
	},

	computed: {
		chartValue() {
			// if (isNaN(this.value)) {
			// 	return '-'
			// }
			return this.value ? Number(this.value).toFixed(2) : this.value
		},

		chartInstance() {
			return this.$refs.chart?.chart
		},
	},
}
</script>


<style scoped>
.chart {
  /* height: 22vh; */
	height: 100%;
}
</style>